import { Directive, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartConfiguration, ChartType } from 'chart.js';
import {
  SalesInsightsInterval,
  SalesInsightsMeasure,
  SalesInsightsSeriesEntry,
  SalesInsightsSeriesResponse
} from '../../models/sales-insights.model';
import { formatSalesInsightsAmount, formatSalesInsightsCompact } from '../../utils/sales-insights-format';
import { salesInsightsLocale } from '../../utils/sales-insights-labels';
import { SalesInsightsThemeColors } from '../../utils/sales-insights-theme';
import { SalesInsightsBaseComponent } from './sales-insights-base.component';

@Directive()
export abstract class SalesInsightsSeriesBaseComponent extends SalesInsightsBaseComponent<SalesInsightsSeriesResponse> {
  protected translate = inject(TranslateService);

  labels: string[] = [];

  get measure(): SalesInsightsMeasure {
    return this.options.measure || 'valueBase';
  }

  get interval(): SalesInsightsInterval {
    return (this.config?.source?.params?.interval as SalesInsightsInterval) || 'month';
  }

  protected defaultSubtitle(): string {
    return `SALES_INSIGHTS_SUBTITLE_${this.interval.toUpperCase()}`;
  }

  protected isEmpty(data: SalesInsightsSeriesResponse | null): boolean {
    return !this.visibleEntries(data).some(
      entry => (entry.current?.[this.measure] ?? 0) !== 0 || (entry.previous?.[this.measure] ?? 0) !== 0
    );
  }

  // Month and quarter series span several calendar years; the chart shows the latest one against the year before.
  protected visibleEntries(data: SalesInsightsSeriesResponse | null): SalesInsightsSeriesEntry[] {
    const entries = data?.entries || [];
    if (this.interval !== 'month' && this.interval !== 'quarter') {
      return entries;
    }
    const year = entries.length ? entries[entries.length - 1].key.substring(0, 4) : null;
    return entries.filter(entry => entry.key.startsWith(year));
  }

  protected hasPrevious(entries: SalesInsightsSeriesEntry[]): boolean {
    return entries.some(entry => entry.previous?.[this.measure] != null);
  }

  formatKey(key: string): string {
    const locale = salesInsightsLocale(this.translate);
    const month = /^(\d{4})-(\d{2})$/.exec(key);
    if (month && this.interval === 'month') {
      return new Date(+month[1], +month[2] - 1, 1).toLocaleDateString(locale, { month: 'short' });
    }
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (day) {
      return new Date(+day[1], +day[2] - 1, +day[3]).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    }
    const quarter = /^\d{4}-?Q(\d)$/i.exec(key);
    if (quarter) {
      return `Q${quarter[1]}`;
    }
    const week = /^\d{4}-?W(\d{1,2})$/i.exec(key);
    if (week) {
      return `${this.translate.instant('SALES_INSIGHTS_WEEK_SHORT')} ${+week[1]}`;
    }
    return key;
  }

  protected seriesLabel(key: string | undefined, fallbackKey: string): string {
    const year = key && /^(\d{4})/.exec(key);
    return year ? year[1] : this.translate.instant(fallbackKey);
  }

  protected baseOptions<TType extends ChartType>(
    theme: SalesInsightsThemeColors
  ): ChartConfiguration<TType>['options'] {
    const options: ChartConfiguration<'bar' | 'line'>['options'] = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'center',
          labels: { color: theme.muted, boxWidth: 12, boxHeight: 12, padding: 16, font: { size: 12 } }
        },
        tooltip: {
          callbacks: {
            label: ctx => ` ${ctx.dataset.label}: ${formatSalesInsightsAmount(ctx.parsed.y)}`
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: theme.muted, font: { size: 12 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 12 }
        },
        y: {
          beginAtZero: true,
          grid: { color: theme.grid, drawBorder: false },
          ticks: {
            color: theme.muted,
            font: { size: 11 },
            maxTicksLimit: 6,
            callback: v => formatSalesInsightsCompact(+v)
          }
        }
      }
    };
    return options as ChartConfiguration<TType>['options'];
  }
}
